import { useEffect, useState, useRef, ComponentType } from "react";
import { motion } from "framer-motion";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { MenuDocument } from "prismicio-types";
import {
  constants,
  styles as moduleStyles,
  animationProps as moduleAnimationProps,
  HyamPrismicDocumentNavBarStyle,
} from "./styles";
import { PrismicNextImage } from "@prismicio/next";
import HamburgerOpen from "hyam-core/svg/hamburger-open-24px.svg";
import {
  HyamPrismicLink,
  HyamPrismicDocumentNavBar_Drawer,
  HyamPrismicButton,
} from "hyam-prismic/components";
import {
  useEventListener,
  dispatchEvent,
  EventOnScrollWindowDetail,
} from "hyam-core/utils/eventTarget";
import { useOnRouteChange } from "hyam-core/hooks/useOnRouteChange";
import { useRouter } from "next/router";
import { withConfigure } from "hyam-core/utils/withConfigure";
import { classNamesWithMerge as cn } from "hyam-core/utils/tailwindMerge";
import { useWindowSize } from "usehooks-ts";

export type HyamPrismicDocumentNavBarProps = {
  document: MenuDocument;
  styles?: Partial<HyamPrismicDocumentNavBarStyle>;
  stylesReplace?: Partial<HyamPrismicDocumentNavBarStyle>;
  langSwitchUi?: "dropdown" | "row";
  Hamburger?: ComponentType;
};

type UseScrollViewportHeightProps = {
  isBelowViewportHeight: () => void;
  isAboveViewportHeight: () => void;
};

const useScrollViewportHeight = (props: UseScrollViewportHeightProps) => {
  const isBelow = useRef(false);

  useEventListener(
    {
      name: "on_scroll_window",
      cb: ({ detail }: { detail: EventOnScrollWindowDetail }) => {
        const isBelowNew = detail.scrollTop > window.innerHeight;

        if (isBelowNew === isBelow.current) return;

        if (isBelowNew) {
          props.isBelowViewportHeight();
        } else {
          props.isAboveViewportHeight();
        }

        isBelow.current = isBelowNew;
      },
    },
    [],
  );
};

const _HyamPrismicDocumentNavBar = (props: HyamPrismicDocumentNavBarProps) => {
  //  langSwitchUi = "dropdown",
  const { document, Hamburger = HamburgerOpen } = props;

  const styles = props.styles as HyamPrismicDocumentNavBarStyle;
  const { asPath } = useRouter();

  const [showBar, setShowBar] = useState(true);
  const [showLinks, setShowLinks] = useState(true);
  const [showTextLogo, setShowTextLogo] = useState(true);
  const [showBorder, setShowBorder] = useState(false);
  const [transparentNavBar, setTransparentNavBar] = useState(false);
  const [showCta, setShowCta] = useState(true);

  const { routeStatusAction } = useOnRouteChange();
  const { data } = document || {};
  const [isScrollTop, setIsScrollTop] = useState(true);
  const isScrollTopRef = useRef(isScrollTop);
  isScrollTopRef.current = isScrollTop;

  const { width: screenWidth } = useWindowSize();

  useScrollPosition(({ currPos }) => {
    const newIsScrollTop = currPos.y > constants.scrollTopThreshold;

    // no need to apply, value didn't change
    if (newIsScrollTop === isScrollTopRef.current) return;

    setIsScrollTop(newIsScrollTop);
  });

  useScrollViewportHeight({
    isAboveViewportHeight: () => console.log("___ isAboveViewportHeight"),
    isBelowViewportHeight: () => console.log("___ isBelowViewportHeight"),
  });

  // show/hide bar
  useEventListener(
    {
      name: "on_scroll_window",
      cb: ({ detail }: { detail: EventOnScrollWindowDetail }) => {
        if (
          detail.scrollTop < constants.hideDesktopNavBarThreshold ||
          (detail.direction === "up" && detail.thresholdUp > constants.showThreshold)
        ) {
          if (!showBar) {
            setShowBar(true);
            dispatchEvent({ name: "nav_bar_is_visible" });
          }
        } else {
          if (showBar) {
            setShowBar(false);
            dispatchEvent({ name: "nav_bar_is_hidden" });
          }
        }

        const screen = window.innerWidth;
        const areLinksVisible = detail.scrollTop < constants.hideTopLinks;
        const isTopOfThePage = detail.scrollTop < constants.hideTextLogo;

        //If a slice has the [data-transparentNavBar] attribute the navbar should be transparent
        const isNavBarTransparent =
          window.document.querySelector("[data-transparentNavBar]") != null;

        //Only above 1280px (xl breakpoint) the links are visible
        if (screen >= 1280) {
          // Hide Links/TextLogo, only when the state change compared to the previous value
          if (showLinks != areLinksVisible) {
            setShowLinks(areLinksVisible);
            setShowBorder(!areLinksVisible);
          }
          if (showTextLogo != isTopOfThePage) {
            setShowTextLogo(isTopOfThePage);
          }
        } else {
          //On mobile the anchor links are not visible
          setShowLinks(false);
          setShowTextLogo(isTopOfThePage);

          //On mobile the navbar is transparent depending on the first slice
          if (isNavBarTransparent && screenWidth < 640) {
            setTransparentNavBar(areLinksVisible);
          } else {
            setTransparentNavBar(false);
          }

          //The border is visible only when the links are hidden
          setShowBorder(!areLinksVisible);
        }
      },
    },
    [showBar],
  );

  const hanldeNavbarState = () => {
    //Only on desktop the links are displayed
    const screen = window.innerWidth;

    if (screen >= 1280 && window.scrollY < constants.hideTopLinks) {
      setShowLinks(true);
      setShowBorder(false);
    } else {
      setShowLinks(false);
    }

    const isNavBarTransparent =
      window.document.querySelector("[data-transparentNavBar]") != null;

    //On mobile the navbar is transparent depending on the first slice
    if (isNavBarTransparent && screen < 640) {
      setTransparentNavBar(true);
    } else {
      setTransparentNavBar(false);
    }
  };

  //Handling the state of the Navbar for:
  //the Resizing, First Load, Page changes and Transparency
  useEffect(() => {
    hanldeNavbarState();
  }, [screenWidth]);

  //Delay to make sure that the page is loaded
  useEffect(() => {
    setTimeout(() => {
      hanldeNavbarState();
    }, 100);
  }, [asPath]);

  const animations = moduleAnimationProps[routeStatusAction];
  const [animationsContainer, setAnimationsContainer] = useState(animations.container);

  useEffect(() => {
    setAnimationsContainer({
      ...moduleAnimationProps.onScroll.container,
      animate: showBar ? "show" : "hidden",
    });
  }, [routeStatusAction, showBar]);

  const styles_container = cn(styles.container, {
    [styles.container_scroll_top]: isScrollTop,
  });

  //Observer to hide the cta if the footer is visible
  useEffect(() => {
    const footerObeserver = new IntersectionObserver((entries) => {
      const intersection = entries.some((e) => e.isIntersecting);
      intersection ? setShowCta(false) : setShowCta(true);
    });

    const footer = window.document.querySelector("#footermobile");
    footer ? footerObeserver.observe(footer) : footerObeserver.unobserve;

    return () => {
      footerObeserver.disconnect();
    };
  }, [screenWidth, asPath, showBar]);

  if (!document?.data) return <div>HyamPrismicDocumentNavBar: Missing data</div>;
  return (
    <>
      <motion.div className={cn(styles_container)} {...animationsContainer}>
        <div
          className={cn(
            styles.background,
            { "!border-transparent": !showBorder },
            { "!bg-transparent": transparentNavBar },
          )}
        ></div>

        <div
          className={styles.menu_container}
          onClick={() => dispatchEvent({ name: "show_nav_bar_drawer" })}
        >
          <Hamburger
            className={cn(styles.hamburger, {
              invert: transparentNavBar,
            })}
          />
          <span className={styles.menu_label}>{data?.menu_label}</span>
        </div>

        <HyamPrismicLink
          className={cn(styles.logo_container, { invert: transparentNavBar })}
          href="/"
        >
          <PrismicNextImage
            height={constants.logoHeight}
            className={styles.logo}
            field={data.logo}
          />
          <motion.p
            transition={{ duration: 0.3 }}
            animate={{ opacity: showTextLogo ? 1 : 0 }}
            initial={{ opacity: 1 }}
            className="whitespace-nowrap uppercase tracking-[0.12rem] font-medium font-body antialiased text-[0.55rem]  mb-0 absolute bottom-1 w-fit mx-auto left-0 right-0"
          >
            {data?.logo_subheadline}
          </motion.p>
        </HyamPrismicLink>

        <div className={styles.buttons_container}>
          <HyamPrismicButton
            variant={"Contained Secondary Button Black"}
            field={data?.cta_link}
            styles={{ container: styles.button }}
          >
            {data?.cta_label}
          </HyamPrismicButton>
        </div>
      </motion.div>
      <HyamPrismicDocumentNavBar_Drawer document={props.document} />

      <motion.div
        initial={{ y: 0 }}
        animate={{ y: showCta ? 0 : "100%" }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="fixed bottom-0 xl:hidden z-60"
      >
        <HyamPrismicLink
          className="bg-hyam-black text-white hyam-text-small mb-0 font-semibold py-[10px] uppercase w-screen text-center "
          field={data.cta_link}
        >
          {data.cta_label}
        </HyamPrismicLink>
      </motion.div>
    </>
  );
};

export const HyamPrismicDocumentNavBar = withConfigure<
  HyamPrismicDocumentNavBarProps,
  HyamPrismicDocumentNavBarStyle
>({
  componentName: "HyamPrismicDocumentNavBar",
  Component: _HyamPrismicDocumentNavBar,
  moduleStyles,
});
